import { EnvironmentInjector, inject, Injectable } from '@angular/core';
import { EventsApiService } from '@simOn/event/element/data-access';
import { SpaceFacade } from '@simOn/space';
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { EventPlacementDraft } from 'feature-event';
import { firstValueFrom, take } from 'rxjs';
import { AgentActionOutcome, CreateEventActionDto, UpdateEventActionDto } from '../model';
import {
  EventDraftSource,
  toEventFormValue,
  toEventRecurrenceEndDate,
  toEventReminders
} from './agent-event-draft.mappers';
import { AgentPanelHostService } from './agent-panel-host.service';

// eslint-disable-next-line @nx/enforce-module-boundaries
type FeatureEventChunk = typeof import('feature-event');
type EventPanel = InstanceType<FeatureEventChunk['EventPanelService']>;

/**
 * Handles the `CreateEvent` and `UpdateEvent` draft cards. Nothing is saved by the agent: the regular event form
 * opens in the right sidenav prefilled from the card (through the same draft input the form uses when it comes
 * back from the 3D walk) and the user reviews and saves it as usual.
 *
 * For an update the CURRENT event is loaded first and the card's changes are applied on top, so the form (and the
 * PUT body it builds) keeps the 3D position and everything the card does not mention.
 *
 * The form lives in `feature-event`, which a feature library may not depend on statically; it is loaded lazily
 * like the event preview.
 */
@Injectable({ providedIn: 'root' })
export class AgentEventDraftService {
  private readonly _host = inject(AgentPanelHostService);
  private readonly _eventsApi = inject(EventsApiService);
  private readonly _spaceFacade = inject(SpaceFacade);
  private _featureEventChunk?: Promise<FeatureEventChunk>;
  private _panelInjector?: EnvironmentInjector;
  /** Settles the outcome of the card whose panel is still open when another card replaces it. */
  private _dismissPending?: () => void;

  async create(action: CreateEventActionDto): Promise<AgentActionOutcome> {
    const { panel, chunk } = await this._openPanel(action.apartmentId);
    const outcome = this._outcomeOf(panel);
    panel.openDraft(
      this._draft(action, { payload: action.payload, assignedForAllValue: chunk.ASSIGNED_FOR_ALL_VALUE })
    );
    return outcome;
  }

  async update(action: UpdateEventActionDto): Promise<AgentActionOutcome> {
    // The panel first: `portalFor` decides whether to navigate by the selected space, so it must see the real one.
    const { panel, chunk } = await this._openPanel(action.apartmentId);
    // Event requests are scoped by the selected space (header interceptor); make sure it is the card's one.
    this._spaceFacade.setSelectedId(action.apartmentId);
    const current = await firstValueFrom(this._eventsApi.getEvent(action.targetId));
    const outcome = this._outcomeOf(panel);
    const draft = this._draft(action, {
      payload: action.payload,
      clearedFields: action.clearedFields,
      current,
      assignedForAllValue: chunk.ASSIGNED_FOR_ALL_VALUE
    });
    panel.openDraft({ ...draft, eventId: action.targetId });
    return outcome;
  }

  private async _openPanel(apartmentId: string): Promise<{ panel: EventPanel; chunk: FeatureEventChunk }> {
    const portal = await this._host.portalFor(apartmentId, ['/space', apartmentId, 'dashboard', 'events']);
    if (!portal) throw new Error('[ai-agent] no panel host for the event form');

    // eslint-disable-next-line @nx/enforce-module-boundaries
    const chunk = await (this._featureEventChunk ??= import('feature-event'));
    // The services are scoped to a page component elsewhere; one instance per opened panel keeps that lifetime.
    this._dismissPending?.();
    this._panelInjector?.destroy();
    this._panelInjector = this._host.createPanelInjector(portal, [chunk.EventPanelService, chunk.EventAccessService]);
    return { panel: this._panelInjector.get(chunk.EventPanelService), chunk };
  }

  /** `saved` once the panel reports a saved event; `dismissed` when another card replaces the panel. */
  private _outcomeOf(panel: EventPanel): Promise<AgentActionOutcome> {
    return new Promise<AgentActionOutcome>((resolve) => {
      const saved = panel.refresh$.pipe(take(1)).subscribe(() => resolve('saved'));
      this._dismissPending = (): void => {
        saved.unsubscribe();
        resolve('dismissed');
      };
    });
  }

  private _draft(action: CreateEventActionDto | UpdateEventActionDto, source: EventDraftSource): EventPlacementDraft {
    return {
      apartmentId: action.apartmentId,
      spaceName: action.apartmentName ?? '',
      value: toEventFormValue(source),
      reminders: toEventReminders(source),
      transformation: source.current?.transformation ?? null,
      recurrenceEndDate: toEventRecurrenceEndDate(source),
      // A prepared change is something to save; the form keeps its save button enabled for a dirty draft.
      isDirty: true
    };
  }
}
